/**
 * リアルタイム対戦画面（T15）。
 *
 * 既存の `Board` コンポーネントを流用し、同じ盤面 UI を使い回す。
 * NPC 専用の UndoButton は表示しない（UndoButton 側の `visible=false` プロップ仕様に準拠）。
 *
 * ScorePanel は「あなた/CPU」ラベル固定のため、マルチ対戦では表示文言を切り替えるため
 * 専用の軽量スコア表示を採用する（既存 ScorePanel の displayName 対応を避けるための選択）。
 *
 * 状態同期:
 *   - `useRealtimeBattle()` hook が WS 接続・メッセージ処理・勝敗判定を担う
 *   - 本コンポーネントは presentational に徹する
 */

import { useEffect, useMemo, useState } from 'react'
import { Board } from './Board'
import { computeScore, computeTerritory, findWinLine, buildFlowers } from '../game/engine'
import { createEmptyFlowers } from '../game/types'
import { generateMap } from '../game/mapGen'
import { useRealtimeBattle } from '../hooks/useRealtimeBattle'
import {
  REALTIME_UI,
  WS_MOVE_TIMEOUT_MS,
  type BattleResult,
} from '../config/realtime'
import { BONUS_PERCENT } from '../config/scoring'
import { leaveRoom } from '../services/rooms'
import type { RoomInfo } from '../config/multiplayer'
import type { LocalProfile } from '../services/hubProfile'
import block1 from '../assets/block1.webp'
import block2 from '../assets/block2.webp'

interface Props {
  room: RoomInfo
  selfUserId: string
  selfProfile: LocalProfile
  apiBaseUrl: string
  onExit: (result: BattleResult | null) => void
}

export function MultiplayerGameScreen({
  room,
  selfUserId,
  selfProfile,
  apiBaseUrl,
  onExit,
}: Props) {
  const [gameOverResult, setGameOverResult] = useState<BattleResult | null>(null)
  // 盤面装飾マップはマウント時に一度だけ生成（演出用・ゲームロジック非関与）
  const [decorMap] = useState(() => generateMap())

  const {
    connectionStatus,
    reconnectAttempt,
    battleSnapshot,
    moveTimeLeft,
    errorMessage,
    selfColor,
    placeMove,
    forfeit,
    reconnect,
    exit,
    close,
  } = useRealtimeBattle({
    room,
    selfUserId,
    selfProfile,
    apiBaseUrl,
    onGameOver: (result) => {
      setGameOverResult(result)
    },
  })

  // 対戦終了後に Hub からルーム退出する（exit ボタンではなく自動クリーンアップ）
  useEffect(() => {
    if (!gameOverResult) return
    // ルームから退出（失敗しても UI は進める）
    void leaveRoom(room.roomId).catch((err) => {
      console.warn('[MultiplayerGameScreen] leaveRoom failed:', err)
    })
  }, [gameOverResult, room.roomId])

  // スコア・territory・winLine を都度 computeScore で導出（engine の純粋関数）
  const derived = useMemo(() => {
    const board = battleSnapshot?.board ?? null
    if (!board) return null
    const blackScore = computeScore(board, 'black')
    const whiteScore = computeScore(board, 'white')
    const territory = {
      black: computeTerritory(board, 'black'),
      white: computeTerritory(board, 'white'),
    }
    const winLine = battleSnapshot?.winner && battleSnapshot.winner !== 'draw'
      ? findWinLine(board, battleSnapshot.winner)
      : null
    const flowers = winLine ? buildFlowers(winLine) : createEmptyFlowers()
    return { blackScore, whiteScore, territory, winLine, flowers }
  }, [battleSnapshot])

  const selfMember = useMemo(() => {
    if (!battleSnapshot) return null
    return battleSnapshot.members.find((m) => m.userId === selfUserId) ?? null
  }, [battleSnapshot, selfUserId])

  const opponentMember = useMemo(() => {
    if (!battleSnapshot) return null
    return battleSnapshot.members.find((m) => m.userId !== selfUserId) ?? null
  }, [battleSnapshot, selfUserId])

  const handleCellClick = (row: number, col: number) => {
    placeMove(row, col)
  }

  const leaveAndClose = async (result: BattleResult | null): Promise<void> => {
    try {
      await leaveRoom(room.roomId)
    } catch (err) {
      console.warn('[MultiplayerGameScreen] leaveRoom failed:', err)
    }
    close()
    onExit(result)
  }

  const handleExitClick = () => {
    if (gameOverResult) {
      void leaveAndClose(gameOverResult)
      return
    }
    // 対戦中の退出は投了とみなす
    if (typeof window !== 'undefined' && !window.confirm(REALTIME_UI.forfeitConfirm)) {
      return
    }
    forfeit()
  }

  const handleGameOverDismiss = () => {
    exit()
    void leaveAndClose(gameOverResult)
  }

  // 手番タイムアウト警告
  const [moveTimeoutWarned, setMoveTimeoutWarned] = useState(false)
  useEffect(() => {
    if (battleSnapshot && !battleSnapshot.gameOver && moveTimeLeft === 0 && !moveTimeoutWarned) {
      setMoveTimeoutWarned(true)
    }
    if (battleSnapshot && moveTimeLeft > 0 && moveTimeoutWarned) {
      setMoveTimeoutWarned(false)
    }
  }, [battleSnapshot, moveTimeLeft, moveTimeoutWarned])

  // --- レンダリング -------------------------------------------------------

  // 接続前の初期化中（ticket 取得 + WebSocket open 前）
  if (connectionStatus === 'idle' || connectionStatus === 'connecting') {
    return (
      <OverlayMessage title={REALTIME_UI.connecting}>
        <div className="multi-screen__spinner">●●●</div>
      </OverlayMessage>
    )
  }

  // 重大エラー（認証失敗 / キック / 再接続失敗）
  if (connectionStatus === 'error') {
    return (
      <OverlayMessage title={REALTIME_UI.connectErrorTitle}>
        <p className="multi-screen__error">{errorMessage ?? '接続に失敗しました'}</p>
        <div className="multi-screen__actions">
          <button type="button" className="btn btn--primary" onClick={reconnect}>
            {REALTIME_UI.retryButton}
          </button>
          <button type="button" className="btn btn--secondary" onClick={() => { void leaveAndClose(null) }}>
            {REALTIME_UI.cancelButton}
          </button>
        </div>
      </OverlayMessage>
    )
  }

  // snapshot 未初期化（相手の StartMatch 待ち or Connected 直後）
  if (!battleSnapshot || !derived || !selfColor) {
    return (
      <OverlayMessage title={REALTIME_UI.waitingOpponent}>
        <div className="multi-screen__spinner">●●●</div>
        <button type="button" className="btn btn--secondary" onClick={() => { void leaveAndClose(null) }}>
          {REALTIME_UI.exitButton}
        </button>
      </OverlayMessage>
    )
  }

  const isPlayerTurn = battleSnapshot.turn === selfColor
  const moveSecLeft = Math.ceil(moveTimeLeft / 1000)

  // ゲーム終了後のオーバーレイ
  const showGameOver = battleSnapshot.gameOver && gameOverResult

  return (
    <div className="screen screen--game">
      {/* スコアヘッダ（マルチ用の軽量版） */}
      <MultiScorePanel
        blackScoreTotal={derived.blackScore.total}
        whiteScoreTotal={derived.whiteScore.total}
        selfColor={selfColor}
        selfDisplayName={selfMember?.displayName ?? selfProfile.displayName}
        opponentDisplayName={opponentMember?.displayName ?? REALTIME_UI.waitingOpponent}
        turn={battleSnapshot.turn}
      />

      {/* ターン表示 + 手番残り時間 */}
      <div className={`turn-banner ${isPlayerTurn ? 'turn-banner--player' : 'turn-banner--cpu'}`}>
        {isPlayerTurn ? REALTIME_UI.yourTurn : REALTIME_UI.opponentTurn}
        <span className="multi-screen__timer" aria-label="残り時間">
          {moveSecLeft}s
        </span>
      </div>

      {moveTimeoutWarned && !battleSnapshot.gameOver && (
        <div className="multi-screen__timeout-warning" role="alert">
          {REALTIME_UI.moveTimeoutWarning}
        </div>
      )}

      {/* 盤面（既存 Board を流用） */}
      <Board
        board={battleSnapshot.board}
        territory={derived.territory}
        winLine={derived.winLine}
        flowers={derived.flowers}
        map={decorMap}
        turnCount={battleSnapshot.turnCount}
        turn={battleSnapshot.turn}
        playerColor={selfColor}
        phase={'game'}
        finaleHighlight={[]}
        finaleSparkle={false}
        scorePopups={[]}
        onCellClick={handleCellClick}
        onPopupComplete={() => { /* noop（マルチ対戦では popups を使わない） */ }}
      />

      <div className="turn-count">
        {battleSnapshot.turnCount}手目
      </div>

      {/* 接続状態 + 退出ボタン */}
      <div className="multi-screen__footer">
        <ConnectionIndicator
          status={connectionStatus}
          reconnectAttempt={reconnectAttempt}
        />
        <button
          type="button"
          className="btn btn--secondary multi-screen__exit"
          onClick={handleExitClick}
          disabled={connectionStatus === 'reconnecting'}
        >
          {REALTIME_UI.exitButton}
        </button>
      </div>

      {/* ゲーム終了オーバーレイ */}
      {showGameOver && gameOverResult && (
        <GameOverOverlay
          result={gameOverResult}
          onDismiss={handleGameOverDismiss}
        />
      )}

      {/* 再接続中オーバーレイ（非ブロッキング） */}
      {connectionStatus === 'reconnecting' && (
        <ReconnectingOverlay attempt={reconnectAttempt} />
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// サブコンポーネント
// ---------------------------------------------------------------------------

interface OverlayMessageProps {
  title: string
  children?: React.ReactNode
}

function OverlayMessage({ title, children }: OverlayMessageProps) {
  return (
    <div className="screen screen--game multi-screen__overlay-wrapper">
      <div className="multi-screen__overlay">
        <h2 className="multi-screen__overlay-title">{title}</h2>
        {children}
      </div>
    </div>
  )
}

interface MultiScorePanelProps {
  blackScoreTotal: number
  whiteScoreTotal: number
  selfColor: 'black' | 'white'
  selfDisplayName: string
  opponentDisplayName: string
  turn: 'black' | 'white'
}

function MultiScorePanel({
  blackScoreTotal,
  whiteScoreTotal,
  selfColor,
  selfDisplayName,
  opponentDisplayName,
  turn,
}: MultiScorePanelProps) {
  const oppColor: 'black' | 'white' = selfColor === 'black' ? 'white' : 'black'
  const selfScore = selfColor === 'black' ? blackScoreTotal : whiteScoreTotal
  const oppScore = selfColor === 'black' ? whiteScoreTotal : blackScoreTotal
  const selfHasKomi = selfColor === 'white'
  const oppHasKomi = oppColor === 'white'
  const selfIcon = selfColor === 'black' ? block1 : block2
  const oppIcon = oppColor === 'black' ? block1 : block2
  return (
    <div className="score-panel">
      <div className={`score-card ${turn === selfColor ? 'score-card--active' : ''}`}>
        <div className="score-card__header">
          <img src={selfIcon} alt={selfDisplayName} className="score-card__icon" />
          <span className="score-card__name">{selfDisplayName}</span>
          {selfHasKomi && (
            <span className="score-card__komi">+{BONUS_PERCENT}%</span>
          )}
          {turn === selfColor && <span className="score-card__turn">▶</span>}
        </div>
        <div className="score-card__total">
          {Math.round(selfScore).toLocaleString()}
          <span className="score-card__unit">pt</span>
        </div>
      </div>

      <div className="score-panel__vs">VS</div>

      <div className={`score-card ${turn === oppColor ? 'score-card--active' : ''}`}>
        <div className="score-card__header">
          <img src={oppIcon} alt={opponentDisplayName} className="score-card__icon" />
          <span className="score-card__name">{opponentDisplayName}</span>
          {oppHasKomi && (
            <span className="score-card__komi">+{BONUS_PERCENT}%</span>
          )}
          {turn === oppColor && <span className="score-card__turn">▶</span>}
        </div>
        <div className="score-card__total">
          {Math.round(oppScore).toLocaleString()}
          <span className="score-card__unit">pt</span>
        </div>
      </div>
    </div>
  )
}

interface ConnectionIndicatorProps {
  status: string
  reconnectAttempt: number
}

function ConnectionIndicator({ status, reconnectAttempt }: ConnectionIndicatorProps) {
  const color =
    status === 'connected' ? '#5fe05f' :
    status === 'reconnecting' ? '#ffb347' :
    status === 'error' ? '#ff6666' :
    'rgba(255,255,255,0.5)'
  const text =
    status === 'connected' ? 'オンライン' :
    status === 'reconnecting' ? `再接続中 (${reconnectAttempt})` :
    status === 'error' ? '接続エラー' :
    '接続準備中…'
  return (
    <div className="multi-screen__indicator" style={{ color }}>
      <span className="multi-screen__indicator-dot" style={{ backgroundColor: color }} />
      {text}
    </div>
  )
}

interface ReconnectingOverlayProps {
  attempt: number
}

function ReconnectingOverlay({ attempt }: ReconnectingOverlayProps) {
  const warnMs = WS_MOVE_TIMEOUT_MS // 30 秒以内に戻らなければ失敗表示に切り替わる
  void warnMs
  return (
    <div className="multi-screen__reconnect-toast" role="status">
      {REALTIME_UI.reconnecting} ({attempt})
    </div>
  )
}

interface GameOverOverlayProps {
  result: BattleResult
  onDismiss: () => void
}

function GameOverOverlay({ result, onDismiss }: GameOverOverlayProps) {
  const title = (() => {
    switch (result.reason) {
      case 'win': return REALTIME_UI.winTitle
      case 'lose': return REALTIME_UI.loseTitle
      case 'draw': return REALTIME_UI.drawTitle
      case 'forfeit': return REALTIME_UI.loseTitle
      case 'opponent_forfeit': return REALTIME_UI.opponentForfeitTitle
      case 'connection_lost': return REALTIME_UI.connectionLostTitle
      case 'exited': return ''
      default: return ''
    }
  })()
  return (
    <div className="multi-screen__gameover">
      <div className="multi-screen__gameover-inner">
        <h2 className="multi-screen__gameover-title">{title}</h2>
        {result.reason === 'connection_lost' && (
          <p className="multi-screen__error">{REALTIME_UI.connectionLostMessage}</p>
        )}
        <div className="multi-screen__actions">
          <button type="button" className="btn btn--primary" onClick={onDismiss}>
            タイトルへ
          </button>
        </div>
      </div>
    </div>
  )
}
